import { useEffect, useState } from 'react'
import { Grid } from 'semantic-ui-react'

import { Alert, Card, GridRowColumn, Text } from '@/componentsV1/BaseComponents'
import DeprecatedTooltip from '@/features/taxes/quarterly/components/DeprecatedTooltip'
import { TransactionHeader } from '@/features/transactions/lists/TransactionHeader'
import { TransactionCard } from '@/features/transactions/lists/TransactionCard'
import { TransactionCategoryIdentifier } from '@/reducers/admin/allTransactions.slice'
import { StepHeaders } from '@/features/bookkeeping/step-header'
import './styles.scss'
import { useReselector } from '@/utils/sharedHooks'
import {
  selectTransactionsByIds,
  selectUserTransactionsByCategoryIdentifierForYear,
} from '@/features/transactions/transactions.selectors'
import { TransactionRuleCategoryType } from '@/reducers/admin/transactionRulesReducer'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'

interface IOwnerDistributionProps {
  setIsStepComplete?: (value: boolean) => void
}

export const OwnersDistribution = ({
  setIsStepComplete,
}: IOwnerDistributionProps) => {
  const taxYear = useReselector(selectCurrentAnnualTaxYear)

  const distributionTransactions = useReselector(
    selectUserTransactionsByCategoryIdentifierForYear,
    TransactionCategoryIdentifier.owners_distribution,
    taxYear
  )

  const [distributionTransactionIds, setDistributionTransactionIds] = useState<
    number[]
  >([])

  useEffect(() => {
    // Still want to display accounts after category updates,
    // so don't update the distributionTransactionsIds list that was initially loaded
    if (distributionTransactions.length && !distributionTransactionIds.length) {
      setDistributionTransactionIds(distributionTransactions.map((t) => t.id))
    }
  }, [distributionTransactionIds.length, distributionTransactions])

  const localTransactions = useReselector(
    selectTransactionsByIds,
    distributionTransactionIds
  )

  useEffect(() => {
    const transactionsCategorized = localTransactions.every(
      (t) =>
        t.type === TransactionRuleCategoryType.personal ||
        (t.type === TransactionRuleCategoryType.business &&
          t.transactionCategory) ||
        t.notes
    )

    setIsStepComplete?.(transactionsCategorized)
  }, [localTransactions, setIsStepComplete])

  return (
    <Grid>
      <GridRowColumn centerContent className="header-container">
        <StepHeaders
          imageSrc="https://heard-images.s3.amazonaws.com/assets/coinsV2.svg"
          imageAlt="coins"
          imageWidth={180}
          imageHeight={180}
          title="Do these Owner’s Distributions look correct?"
          description=""
          customColor="yellow"
        >
          <Grid>
            <GridRowColumn className="align-left">
              <Text>
                We categorized these expenses as <b>Owner’s Distributions</b>,
                or business funds that you took out for personal use. If
                something doesn’t look right, please add a note to explain why.
              </Text>
            </GridRowColumn>
            <GridRowColumn width={12} className="tooltip-grid">
              <DeprecatedTooltip
                popup={{
                  title: 'Owner’s Distributions',
                  body: 'Funds that you draw from your business checking account into your personal checking account, and are used solely for personal purposes (no business-related expenses).<br/>For example, paying yourself is an Owner’s Distribution.',
                }}
                labelComponent="What is an Owner’s Distribution?"
              />
            </GridRowColumn>
          </Grid>
        </StepHeaders>
      </GridRowColumn>
      <Grid.Row />
      {distributionTransactionIds?.length > 0 && <TransactionHeader />}
      <GridRowColumn short>
        {distributionTransactionIds.map((tId) => (
          <TransactionCard key={tId} transactionId={tId} />
        ))}
      </GridRowColumn>
      {distributionTransactionIds?.length === 0 && (
        <GridRowColumn width={10} spacer={3}>
          <Card backgroundColor="lightGreen">
            <Text>
              All transactions have been recategorized. You may continue to the
              next step.
            </Text>
          </Card>
        </GridRowColumn>
      )}
      <GridRowColumn width={10} spacer={3} className="alert-footer">
        <Alert title="Why are we asking this?">
          Sometimes these transactions get miscategorized. For example, you may
          have reimbursed yourself for a business expense you paid for on a
          personal card. It’s categorized as an Owner’s Distribution, but it’s
          actually a business expense.
        </Alert>
      </GridRowColumn>
    </Grid>
  )
}
